import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import {
  extractErrorMessage,
  PageContainer,
  PageTopbar,
  StickyActionBar,
  astryx,
  neutralTheme,
  useDocumentTheme,
  type BreadcrumbItem,
} from "@shadowcue/ui";
import UserForm, { type IUserForm } from "./form";

interface UserEditProps {
  currentUser: IUser;
}

// Seeded with blankUser (rather than null) so UserForm below can read
// formValues unconditionally — ContentContainer's `children` prop is
// constructed on every render regardless of `loading`, so a null initial
// value would throw before the fetch below ever resolves.
const blankUser: IUserForm = {
  email: "",
  password: "",
  passwordConfirm: "",
  admin: false,
};

const axios = newAxiosClient();


const UserEdit = ({ currentUser }: UserEditProps) => {
  const [formValues, setFormValues] = useState<IUserForm>(blankUser);
  const [loading, setLoading] = useState<boolean>(true);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const params = useParams();
  const navigate = useNavigate();
  const mode = useDocumentTheme();

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Users", href: "/admin/users" },
    { label: "Edit" },
  ];

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const res = await axios.get(`/api/users/${params.userId}`, {
          headers: {
            AUTHORIZATION: `Bearer ${currentUser.authToken}`,
          },
        });
        const user = res.data.user;
        setFormValues({
          id: user.id,
          email: user.email,
          password: "",
          passwordConfirm: "",
          admin: user.admin,
        });
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, [params.userId]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setSubmitError(null);

    // Check password confirmation match
    if (formValues.password !== formValues.passwordConfirm) {
      setSubmitError("Password and password confirmation must match")
      setSubmitting(false)
      return
    }
    try {
      // `admin` must always be included in the payload — the API removes
      // the user's admin role whenever an update request omits this param.
      await axios.put(
        `/api/users/${params.userId}`,
        { user: formValues },
        {
          headers: { AUTHORIZATION: `Bearer ${currentUser.authToken}` },
          withCredentials: true,
        },
      );
      navigate("/admin/users");
    } catch (error) {
      setSubmitError(
        extractErrorMessage(error, "Failed to save the user. Please try again."),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <UserForm
          values={formValues}
          onChange={setFormValues}
          loading={loading}
          isEditing
        />

        <StickyActionBar error={submitError ?? undefined}>
          <astryx.Button
            label="Cancel"
            variant="ghost"
            onClick={() => navigate("/admin/users")}
          />
          <astryx.Button
            label="Save"
            variant="primary"
            isLoading={submitting}
            onClick={handleSubmit}
          />
        </StickyActionBar>
      </PageContainer>
    </astryx.Theme>
  );
};

export default UserEdit;
